import { Save } from "@mui/icons-material";
import { AccordionDetails, Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";
import { v4 as uuid } from "uuid";

import { GqlPermissions, GqlProtocolVisit, ProtocolUpdateProtocolVisitInput } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { convertCentsToEuros } from "~/modules/app/util/convertCentsToEuros.ts";
import { convertSecondsToHours } from "~/modules/app/util/convertSecondsToHours.ts";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import useProtocolVisitDelete from "../../routes/ProtocolEditRoute/api/useProtocolVisitDelete";
import useProtocolVisitUpdate from "../../routes/ProtocolEditRoute/api/useProtocolVisitUpdate";
import ProtocolVisitConditionsEditor from "../ProtocolVisitConditionsEditor";
import ProtocolVisitsPricingEditor from "../ProtocolVisitsPricingEditor";
import ProtocolVisitsWeatherEditor from "../ProtocolVisitsWeatherEditor";
import ProtocolVistsTimeslotsEditor from "../ProtocolVisitTimeslotsEditor";
import { VisitEditFormSection } from "./VisitEditFormSection";

type Props = {
    protocol: { id: string };
    visit: GqlProtocolVisit;
    allVisits: GqlProtocolVisit[];
    onSave?: () => void;
};

// The form edits the duration in hours (slider with half-hour steps) and the price in euros; the API stores whole seconds and cents.
export type ProtocolVisitFormInput = Omit<
    ProtocolUpdateProtocolVisitInput,
    "protocolVisitId" | "minDurationInSeconds" | "pricePerHourInCents"
> & {
    minDurationInHours: number;
    pricePerHourInEuros: number;
};

export default function VisitEditForm({ protocol, visit, allVisits, onSave }: Props) {
    const { t } = useTranslation("protocol");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const { updateVisit, error } = useProtocolVisitUpdate(protocol.id);

    const getDefaultValues = () => {
        return {
            notes: visit.notes,
            condition: visit.condition ? { ...visit.condition } : { type: "and", id: uuid() },
            timeslots: visit.timeslots,
            minDurationInHours: visit.minDurationInSeconds ? convertSecondsToHours(visit.minDurationInSeconds) : 2,
            pricePerHourInEuros: convertCentsToEuros(visit.pricePerHourInCents ?? 8500),
            name: visit.name,
            description: visit.description,
            calculateTravelCost: visit.calculateTravelCost ?? true,
            minTemperature: visit.minTemperature ?? -10,
            maxTemperature: visit.maxTemperature ?? 30,
            maxWindForce: visit.maxWindForce ?? 10,
            maxPrecipitation: visit.maxPrecipitation ?? 6,
        };
    };

    const formContext = useForm<ProtocolVisitFormInput>({
        defaultValues: getDefaultValues(),
    });

    const {
        handleSubmit,
        setError,
        control,
        register,
        formState: { errors, isLoading, isDirty, isSubmitted, isSubmitting },
    } = formContext;

    const onSubmit = async ({ minDurationInHours, pricePerHourInEuros, ...data }: ProtocolVisitFormInput) => {
        await updateVisit({
            ...data,
            pricePerHourInCents: Math.round(Number(pricePerHourInEuros) * 100),
            minDurationInSeconds: Math.round(Number(minDurationInHours) * 3600),
            protocolVisitId: visit.id,
        });
        onSave?.();
    };

    const { deleteVisit, loading: deleteLoading, error: deleteError } = useProtocolVisitDelete(protocol.id);

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);
    useApplyErrorToForm(error, setError);

    return (
        <AccordionDetails sx={{ padding: 0 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack>
                    <GaiaConfirmCloseDialog blocker={blocker} />

                    <VisitEditFormSection title={t("protocolVisitsEditor.timeslotEditorTitle")}>
                        <ProtocolVistsTimeslotsEditor control={control} />
                    </VisitEditFormSection>

                    <VisitEditFormSection title={t("protocolVisitsEditor.conditionsEditorTitle")}>
                        <Controller
                            name="condition"
                            control={control}
                            render={({ field }) => (
                                <ProtocolVisitConditionsEditor
                                    allVisits={allVisits}
                                    condition={field.value}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </VisitEditFormSection>

                    <VisitEditFormSection title={t("protocolVisitsEditor.weatherEditorTitle")}>
                        <ProtocolVisitsWeatherEditor formContext={formContext} />
                    </VisitEditFormSection>

                    <VisitEditFormSection title={t("protocolVisitsEditor.pricingEditorTitle")}>
                        <ProtocolVisitsPricingEditor formContext={formContext} />
                    </VisitEditFormSection>

                    <VisitEditFormSection title="">
                        <Stack spacing={2}>
                            <TextField
                                disabled={!isAdmin}
                                label={t("protocolVisitsEditor.name")}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                                {...register("name")}
                            />

                            <TextField
                                disabled={!isAdmin}
                                label={t("protocolVisitsEditor.description")}
                                error={!!errors.description}
                                helperText={errors.description?.message}
                                multiline
                                minRows={3}
                                {...register("description")}
                            />
                        </Stack>

                        {(deleteError || errors.root) && (
                            <Alert severity="error">
                                {deleteError && <Typography>{deleteError.message}</Typography>}
                                {errors.root && <Typography>{errors.root.message}</Typography>}
                            </Alert>
                        )}

                        {isAdmin && (
                            <Stack direction="row" justifyContent="space-between" alignItems="bottom" sx={{ mt: 2 }}>
                                <GaiaDeleteButton
                                    isLoading={deleteLoading}
                                    onDelete={() => deleteVisit(visit.id)}
                                    label={t("protocolVisitsEditor.btnDeleteVisit")}
                                />
                                <Button type="submit" color="primary" variant="contained" disabled={!isDirty} loading={isLoading}>
                                    <Save />
                                    {t("protocolVisitsEditor.btnSave")}
                                </Button>
                            </Stack>
                        )}
                    </VisitEditFormSection>
                </Stack>
            </form>
        </AccordionDetails>
    );
}
